/**
 * Cliente UDV AI Copilot → Gemini (directo en developer) o proxy Controller (prod).
 * Contrato: docs/CONTRATO-UDV-AI-COPILOT.md
 */

import { isDeveloperMode } from '../plan/resolveAccess'
import {
  getControllerUrl,
  loadControllerSession,
  authHeadersController,
} from './controllerAuth'

export const COPILOT_MODELS = {
  // 2.5-* ya no acepta keys/proyectos nuevos (404). Preferir familia 3.x.
  // Flash-Lite ~1.5–2 s; 3.5/3.6 Flash suelen ir a 5–8 s en visión.
  FLASH: 'gemini-3.1-flash-lite',
  PRO: 'gemini-3.5-flash',
  MEDGEMMA: 'medgemma-local',
}

export const COPILOT_MODEL_OPTIONS = [
  {
    id: COPILOT_MODELS.FLASH,
    label: 'Gemini 3.1 Flash-Lite (Rápido — Ideal RX)',
    enabled: true,
  },
  {
    id: COPILOT_MODELS.PRO,
    label: 'Gemini 3.5 Flash (Más precisión — TC/RM/Mamo)',
    enabled: true,
  },
  {
    id: COPILOT_MODELS.MEDGEMMA,
    label: 'MedGemma Local (Offline)',
    enabled: false,
  },
]

export const TRIAGE_LEVELS = ['GREEN', 'YELLOW', 'RED']

export const COPILOT_SYSTEM_PROMPT = `Actúa como un médico radiólogo subespecialista y asistente experto de la SUITE TJS en el visor UDV.

Analiza la imagen médica provista y genera un preinforme clínico preliminar y una clasificación de triage de urgencia.
La imagen puede ser un mosaico de varios viewports del layout (p. ej. 1x2 o 2x2): cada panel es una vista/serie distinta con su ventaneo y zoom actuales. Integra los hallazgos de todos los paneles visibles.

DEBES RESPONDER ÚNICAMENTE EN FORMATO JSON VÁLIDO Siguiendo este esquema estricto:

{
  "triage_level": "RED | YELLOW | GREEN",
  "urgency_reason": "Razón breve si es RED o YELLOW, o 'Estudio de rutina sin hallazgos agudos' si es GREEN",
  "hallazgos": "Descripción detallada y técnica de las estructuras visualizadas, estructuras anatómicas, presencia o ausencia de lesiones, infiltrados, trazos de fractura, derrames o alteración de partes blandas.",
  "impresion_diagnostica": "Conclusión concisa y sintética de los hallazgos principales.",
  "recomendacion": "Estudio complementario o seguimiento sugerido si aplica."
}

Instrucciones adicionales:
1. Mantén un lenguaje médico radiológico profesional, preciso y directo.
2. Si la imagen presenta artefactos o mala calidad de ventana, indícalo en hallazgos.
3. Si hay varios paneles, menciona hallazgos por panel cuando aporte valor clínico (izq/der o superior/inferior).
4. No agregues texto introductorio ni explicaciones fuera del objeto JSON.`

export const COPILOT_ERROR = {
  NO_KEY: 'no_key',
  PROXY_UNAVAILABLE: 'proxy_unavailable',
  HTTP: 'http',
  INVALID_JSON: 'invalid_json',
  MEDGEMMA_UNAVAILABLE: 'medgemma_unavailable',
  EMPTY: 'empty',
  TIMEOUT: 'timeout',
}

/** Timeout red Gemini / proxy (ms). */
export const COPILOT_FETCH_TIMEOUT_MS = 45000

/**
 * Mapea IDs viejos (2.5 / 3.6 / pro-preview) al motor actual.
 * Evita que el state de React siga mandando un modelo lento tras HMR.
 * @param {string} [modelVersion]
 * @returns {string}
 */
export function resolveCopilotModelId(modelVersion) {
  const raw = String(modelVersion || '').trim()
  if (raw === COPILOT_MODELS.MEDGEMMA) return raw
  if (raw === COPILOT_MODELS.FLASH || raw === COPILOT_MODELS.PRO) return raw
  const lower = raw.toLowerCase()
  if (!lower) return COPILOT_MODELS.FLASH
  if (lower.includes('pro') && !lower.includes('flash')) return COPILOT_MODELS.PRO
  if (lower.includes('3.5-flash') && !lower.includes('lite')) {
    return COPILOT_MODELS.PRO
  }
  return COPILOT_MODELS.FLASH
}

/**
 * @returns {string}
 */
export function getGeminiApiKeyDev() {
  if (!isDeveloperMode()) return ''
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_GEMINI_API_KEY
    ) {
      return String(process.env.REACT_APP_GEMINI_API_KEY).trim()
    }
  } catch (e) {
    /* ignore */
  }
  return ''
}

/**
 * @param {{ forceProxy?: boolean, forceDirect?: boolean }} [opts]
 * @returns {'direct'|'proxy'}
 */
export function resolveCopilotTransport(opts = {}) {
  if (opts.forceProxy) return 'proxy'
  if (opts.forceDirect) return 'direct'
  if (isDeveloperMode() && getGeminiApiKeyDev()) return 'direct'
  return 'proxy'
}

/**
 * @param {*} raw
 * @returns {{ triage_level: string, urgency_reason: string, hallazgos: string, impresion_diagnostica: string, recomendacion: string }}
 */
export function normalizeCopilotResult(raw) {
  if (!raw || typeof raw !== 'object') {
    const err = new Error(COPILOT_ERROR.INVALID_JSON)
    err.code = COPILOT_ERROR.INVALID_JSON
    throw err
  }
  let level = String(raw.triage_level || '')
    .trim()
    .toUpperCase()
  if (level === 'G') level = 'GREEN'
  if (level === 'Y') level = 'YELLOW'
  if (level === 'R') level = 'RED'
  if (!TRIAGE_LEVELS.includes(level)) {
    const err = new Error(COPILOT_ERROR.INVALID_JSON)
    err.code = COPILOT_ERROR.INVALID_JSON
    throw err
  }
  return {
    triage_level: level,
    urgency_reason: String(raw.urgency_reason || '').trim(),
    hallazgos: String(raw.hallazgos || '').trim(),
    impresion_diagnostica: String(raw.impresion_diagnostica || '').trim(),
    recomendacion: String(raw.recomendacion || '').trim(),
  }
}

/**
 * @param {string} text
 */
export function parseCopilotJsonText(text) {
  const raw = String(text || '').trim()
  if (!raw) {
    const err = new Error(COPILOT_ERROR.EMPTY)
    err.code = COPILOT_ERROR.EMPTY
    throw err
  }
  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    // Intentar extraer bloque JSON si el modelo añadió ruido
    const m = raw.match(/\{[\s\S]*\}/)
    if (!m) {
      const err = new Error(COPILOT_ERROR.INVALID_JSON)
      err.code = COPILOT_ERROR.INVALID_JSON
      throw err
    }
    try {
      parsed = JSON.parse(m[0])
    } catch (e2) {
      const err = new Error(COPILOT_ERROR.INVALID_JSON)
      err.code = COPILOT_ERROR.INVALID_JSON
      throw err
    }
  }
  return normalizeCopilotResult(parsed)
}

/**
 * @param {*} data - respuesta generateContent
 */
export function extractTextFromGeminiResponse(data) {
  try {
    const parts =
      data &&
      data.candidates &&
      data.candidates[0] &&
      data.candidates[0].content &&
      data.candidates[0].content.parts
    if (!Array.isArray(parts) || !parts.length) return ''
    // Con thinking, parts[0] puede ser thought sin text usable.
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i]
      if (!p || p.thought) continue
      const text = String(p.text || '').trim()
      if (text) return text
    }
    return String((parts[0] && parts[0].text) || '')
  } catch (e) {
    return ''
  }
}

function buildGeminiPayload(base64Image, opts = {}) {
  const generationConfig = {
    responseMimeType: 'application/json',
    temperature: 0.1,
    maxOutputTokens: 1024,
  }
  // Flash-Lite no necesita thinking; 3.5+ sí baja latencia con minimal.
  if (opts.withThinkingMinimal) {
    generationConfig.thinkingConfig = { thinkingLevel: 'minimal' }
  }
  return {
    contents: [
      {
        parts: [
          {
            inlineData: {
              mimeType: 'image/jpeg',
              data: base64Image,
            },
          },
          { text: COPILOT_SYSTEM_PROMPT },
        ],
      },
    ],
    generationConfig,
  }
}

/**
 * @param {typeof fetch} fetchImpl
 * @param {string} url
 * @param {RequestInit} init
 * @param {number} [timeoutMs]
 */
async function fetchWithTimeout(
  fetchImpl,
  url,
  init,
  timeoutMs = COPILOT_FETCH_TIMEOUT_MS
) {
  const ms = Math.max(3000, Number(timeoutMs) || COPILOT_FETCH_TIMEOUT_MS)
  if (typeof AbortController === 'undefined') {
    return fetchImpl(url, init)
  }
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  try {
    return await fetchImpl(url, { ...init, signal: ctrl.signal })
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.code === 'ABORT_ERR')) {
      const err = new Error(COPILOT_ERROR.TIMEOUT)
      err.code = COPILOT_ERROR.TIMEOUT
      throw err
    }
    throw e
  } finally {
    clearTimeout(timer)
  }
}

/**
 * @param {string} base64Image
 * @param {string} modelVersion
 * @param {{ fetchImpl?: typeof fetch, apiKey?: string, timeoutMs?: number }} [opts]
 * @returns {Promise<{ triage_level: string, urgency_reason: string, hallazgos: string, impresion_diagnostica: string, recomendacion: string, model_used?: string }>}
 */
async function solicitarDirecto(base64Image, modelVersion, opts = {}) {
  const apiKey = opts.apiKey != null ? opts.apiKey : getGeminiApiKeyDev()
  if (!apiKey) {
    const err = new Error(COPILOT_ERROR.NO_KEY)
    err.code = COPILOT_ERROR.NO_KEY
    throw err
  }
  const fetchImpl = opts.fetchImpl || fetch
  const primary = resolveCopilotModelId(modelVersion)
  // Ante 503/429: probar YA el siguiente modelo (Gemini satura por modelo).
  const candidates = []
  const pushUnique = (m) => {
    if (m && candidates.indexOf(m) < 0) candidates.push(m)
  }
  pushUnique(primary)
  pushUnique(COPILOT_MODELS.FLASH)
  pushUnique(COPILOT_MODELS.PRO)

  let lastErr = null
  for (let c = 0; c < candidates.length; c++) {
    const model = candidates[c]
    const isLast = c === candidates.length - 1
    const maxAttempts = isLast ? 3 : 1
    const withThinkingMinimal = model === COPILOT_MODELS.PRO
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
      model
    )}:generateContent?key=${encodeURIComponent(apiKey)}`

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let response
      try {
        response = await fetchWithTimeout(
          fetchImpl,
          url,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
              buildGeminiPayload(base64Image, { withThinkingMinimal })
            ),
          },
          opts.timeoutMs
        )
      } catch (e) {
        lastErr = e
        if (e && e.code === COPILOT_ERROR.TIMEOUT) {
          if (!isLast) break
          throw e
        }
        break
      }
      if (response.ok) {
        const data = await response.json()
        const parsed = parseCopilotJsonText(extractTextFromGeminiResponse(data))
        if (model !== primary && typeof console !== 'undefined' && console.info) {
          console.info(
            'UDV AI Copilot: fallback',
            primary,
            '→',
            model,
            '(503/429 en primario)'
          )
        }
        parsed.model_used = model
        return parsed
      }
      const status = response.status
      const err = new Error(COPILOT_ERROR.HTTP)
      err.code = COPILOT_ERROR.HTTP
      err.status = status
      lastErr = err
      if (status === 503 || status === 429) {
        if (attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
          continue
        }
        break
      }
      // 4xx distintos de 429: no tiene sentido reintentar otros modelos salvo 404 modelo
      if (status === 404 && !isLast) break
      throw err
    }
  }
  throw (
    lastErr ||
    Object.assign(new Error(COPILOT_ERROR.HTTP), { code: COPILOT_ERROR.HTTP })
  )
}

/**
 * @param {string} base64Image
 * @param {string} modelVersion
 * @param {{ fetchImpl?: typeof fetch, baseUrl?: string, session?: * }} [opts]
 */
async function solicitarProxy(base64Image, modelVersion, opts = {}) {
  const fetchImpl = opts.fetchImpl || fetch
  const base = (opts.baseUrl || getControllerUrl()).replace(/\/+$/, '')
  const url = `${base}/api/v1/ai/copilot/preinforme`
  const session = opts.session || loadControllerSession()
  const model = resolveCopilotModelId(modelVersion)
  let response
  try {
    response = await fetchWithTimeout(fetchImpl, url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeadersController(session),
      },
      body: JSON.stringify({
        model,
        image_jpeg_base64: base64Image,
        mime_type: 'image/jpeg',
      }),
    }, opts.timeoutMs)
  } catch (e) {
    if (e && e.code === COPILOT_ERROR.TIMEOUT) throw e
    const err = new Error(COPILOT_ERROR.PROXY_UNAVAILABLE)
    err.code = COPILOT_ERROR.PROXY_UNAVAILABLE
    err.cause = e
    throw err
  }
  if (!response.ok) {
    const err = new Error(COPILOT_ERROR.HTTP)
    err.code = COPILOT_ERROR.HTTP
    err.status = response.status
    throw err
  }
  const data = await response.json()
  // Proxy puede devolver el objeto ya parseado o { result } / texto Gemini
  if (data && data.triage_level) {
    return normalizeCopilotResult(data)
  }
  if (data && data.result) {
    return normalizeCopilotResult(data.result)
  }
  if (data && data.candidates) {
    return parseCopilotJsonText(extractTextFromGeminiResponse(data))
  }
  if (data && typeof data.text === 'string') {
    return parseCopilotJsonText(data.text)
  }
  const err = new Error(COPILOT_ERROR.INVALID_JSON)
  err.code = COPILOT_ERROR.INVALID_JSON
  throw err
}

/**
 * Envía captura HD y obtiene preinforme + triage.
 * @param {string} base64Image
 * @param {string} [modelVersion]
 * @param {{ forceProxy?: boolean, forceDirect?: boolean, fetchImpl?: typeof fetch, apiKey?: string, baseUrl?: string, session?: * }} [opts]
 */
export async function solicitarPreinformeCopilot(
  base64Image,
  modelVersion = COPILOT_MODELS.FLASH,
  opts = {}
) {
  const model = resolveCopilotModelId(modelVersion)
  if (model === COPILOT_MODELS.MEDGEMMA) {
    const err = new Error(COPILOT_ERROR.MEDGEMMA_UNAVAILABLE)
    err.code = COPILOT_ERROR.MEDGEMMA_UNAVAILABLE
    throw err
  }
  if (!base64Image) {
    const err = new Error(COPILOT_ERROR.EMPTY)
    err.code = COPILOT_ERROR.EMPTY
    throw err
  }
  const transport = resolveCopilotTransport(opts)
  if (transport === 'direct') {
    return solicitarDirecto(base64Image, model, opts)
  }
  return solicitarProxy(base64Image, model, opts)
}
